import React, { useState, useEffect } from 'react';
import api from 'config/axiosConfig';
import { Entity, Comment as CommentData } from 'util/constants';
import Comment from 'components/Comments/Comment';
import EntityCompactCard from 'components/Entities/EntityCompactCard';
import LoadingSkeleton from 'ui/LoadingSkeleton';
import NotFoundPage from 'components/ResponsePages/404NotFoundPage';
import { AxiosError } from 'axios';
import { Repeat } from 'lucide-react';
import { apiRoutes } from "util/apiRoutes";
import { PostFullRenderer } from "components/Posts/display/PostRendererRegistry";
import { resolvePostKind } from "components/Posts/display/resolvePostKind";

interface CrosspostCardProps {
    crosspostComment: CommentData; // The crosspost comment with is_cross_post: true
}

const CrosspostCard: React.FC<CrosspostCardProps> = ({ crosspostComment }) => {
    const [sourceEntity, setSourceEntity] = useState<Entity | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    // Extract source entity info from crosspost comment properties
    const sourceEntityType = crosspostComment.properties?.cross_post_source_type;
    const sourceEntityId = crosspostComment.properties?.cross_post_source_id;
    const crosspostContent = crosspostComment.content?.trim();
    const hasContent = crosspostContent && crosspostContent !== '<p></p>' && crosspostContent !== '';

    useEffect(() => {
        const fetchSourceEntity = async () => {
            if (!sourceEntityType || !sourceEntityId) {
                setError('Invalid crosspost data');
                setLoading(false);
                return;
            }

            try {
                setLoading(true);
                const response = await api.get(apiRoutes.entities.root, {
                    params: {
                        entityType: sourceEntityType,
                        entityIds: sourceEntityId,
                        limit: 1,
                        offset: 0
                    }
                });

                if (response.data && response.data.length > 0) {
                    setSourceEntity(response.data[0]);
                } else {
                    setError('Source entity not found');
                }
            } catch (err) {
                console.error('Error fetching source entity:', err);
                if (err instanceof AxiosError && err.response?.status === 404) {
                    setError('Source entity not found');
                } else {
                    setError('Failed to load crosspost source');
                }
            } finally {
                setLoading(false);
            }
        };

        fetchSourceEntity();
    }, [sourceEntityType, sourceEntityId]);

    const renderSourceEntityCard = (entity: Entity) => {
        const { entity_type, attributes, eid } = entity;
        const showActions = !hasContent; // Show actions only if there's no crosspost content
        switch (entity_type) {
            case 'issue':
                return (
                    <EntityCompactCard
                        key={eid}
                        entity={attributes}
                        displayEntity={attributes}
                        entityType="issue"
                        showMeta={true}
                        showActions={showActions}
                        onClick={() => {}}
                    />
                );
            case 'comment':
                return (
                    <Comment
                        key={eid}
                        {...attributes}
                        depth={0}
                        maxDepth={0}
                        isAllCollapsed={true}
                        isEntitySection={false}
                        showEntityCompactCard={true}
                        showActions={showActions}
                        onAddReply={() => {}}
                        onUpdateComment={() => {}}
                        onDeleteComment={() => {}}
                        commentable_type={attributes.commentable_type}
                        commentable_id={attributes.commentable_id}
                    />
                );
            default:
                if (resolvePostKind(entity_type, attributes)) {
                    return (
                        <PostFullRenderer
                            key={eid}
                            entity={attributes}
                            entityType={entity_type}
                            context="crosspost"
                            showActions={showActions}
                        />
                    );
                }

                return (
                    <NotFoundPage 
                        displayStyle="page" 
                        errorMessage="Sorry, we couldn't find this type of post." 
                    />
                );
        }
    };

    if (loading) {
        return <LoadingSkeleton count={1} variant="entity-card" margin="mx-0" />;
    }

    if (error || !sourceEntity) {
        return null;
    }

    return (
        <div className="crosspost-container pl-1">
            <div className="grid grid-cols-[auto,1fr] items-start min-w-0">
                <div className="flex flex-col h-[calc(100%-0px)] w-full items-center justify-center">
                    <Repeat className="mt-5 size-4 items-start text-gray-500 dark:text-gray-300 rounded-full" />
                    <div 
                        data-tree-line-id={crosspostComment.id}
                        className={`ml-0.5 mt-0 pb-0 mb-0 top-[0px] flex-grow justify-end text-white items-end place-items-end border-r-2 border-t-2 -translate-x-1/2 transform comment-branch-line-unhovered border-solid `}
                    />
                </div>
            {/* Render the source entity card */}
            <div className="min-w-0">
                {renderSourceEntityCard(sourceEntity)}
            </div>
            </div>
            {/* Render the crosspost comment if there's content */}
                <div className="ml-3 my-0">
                    <Comment
                        {...crosspostComment}
                        depth={0}
                        maxDepth={0}
                        isAllCollapsed={true}
                        isEntitySection={true}
                        onAddReply={() => {}}
                        onUpdateComment={() => {}}
                        onDeleteComment={() => {}}
                        commentable_type={crosspostComment.commentable_type}
                        commentable_id={crosspostComment.commentable_id}
                        showActions={hasContent ? true : false}
                    />
                </div>
        </div>
    );
};

export default CrosspostCard; 